import React, { useEffect } from "react";
import { NotificationTimeline } from "../components/dashboard/notificationTimeline";
import { NumberEmployee } from "../components/dashboard/numberEmployee";
import { Heatmap } from "../components";
import { DailyAlerts } from "../components/dashboard/dailyAlerts";
import { CreatedTrajet } from "../components/dashboard/createdTrajet";
import { B2BOnboarding } from "../components/onboarding/B2BOnboarding";
import axiosInstance from "../api/axios-config";
import {
  Box,
  Container,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha
} from "@mui/material";
import { useAuth } from "../contexts/auth-context";
import { useTranslate } from "@refinedev/core";
import { Global } from '@emotion/react';
import { MapPin, Activity } from "lucide-react";

const DashboardHome = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const { isAuthenticated, user } = useAuth();
  const [heatmapData, setHeatmapData] = React.useState<any>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Check if current user is VIGILANE
  const isVigilaneUser = user?.role === 'VIGILANE';

  useEffect(() => {
    const getAlerts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!isAuthenticated || !user) {
          setError(translate("auth.status.notAuthenticated"));
          return;
        }

        const { data: results } = await axiosInstance.get(
          `/alert-history/heatmap`
        );

        setHeatmapData(results);
      } catch (error: any) {
        setError(error.response?.data?.message || error.message || 'Failed to fetch heatmap data');
        setHeatmapData([]);
      } finally {
        setIsLoading(false);
      }
    };

    getAlerts();
  }, [isAuthenticated, user]);

  // Si l'utilisateur a le rôle GUEST, afficher l'onboarding B2B
  if (user?.role === 'GUEST') {
    return <B2BOnboarding />;
  }

  return (
    <>
      <Global styles={`
        .MuiGrid-container > .MuiGrid-item .MuiCardContent-root:last-child {
          padding-bottom: 0px !important;
        }
      `} />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Dashboard Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1,
              fontFamily: 'Inter, sans-serif'
            }}
          >
            {translate('dashboard.title', 'Dashboard')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontFamily: 'Inter, sans-serif' }}
          >
            {translate('dashboard.welcome', 'Welcome back, here\'s what\'s happening today')}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              fontFamily: 'Inter, sans-serif'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Dashboard Grid */}
        <Grid container spacing={3}>
          {/* Top Row: Daily Alerts, Number of Employees, Created Journeys */}
          <Grid item xs={12} md={4}>
            <Card
              // raised={true}
              sx={{
                p: 0,
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' ?
                  '0 4px 20px rgba(0, 0, 0, 0.3)' :
                  '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                height: 280
              }}
            >
              <CardContent sx={{ p: 0, pb: 0, height: '100%' }}>
                <DailyAlerts />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' ?
                  '0 4px 20px rgba(0, 0, 0, 0.3)' :
                  '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                height: 280
              }}
            >
              <CardContent sx={{ p: 0, pb: 0, height: '100%' }}>
                <NumberEmployee />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' ?
                  '0 4px 20px rgba(0, 0, 0, 0.3)' :
                  '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                height: 280
              }}
            >
              <CardContent sx={{ p: 0, pb: 0, height: '100%' }}>
                <CreatedTrajet />
              </CardContent>
            </Card>
          </Grid>

          {/* Second Row: Heatmap and Notification Timeline */}
          <Grid item xs={12} lg={8}>
            {isLoading ? (
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: theme.palette.mode === 'dark' ?
                    '0 4px 20px rgba(0, 0, 0, 0.3)' :
                    '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {translate('dashboard.heatmap_loading', 'Chargement de la carte...')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : error ? (
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: theme.palette.mode === 'dark' ?
                    '0 4px 20px rgba(0, 0, 0, 0.3)' :
                    '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="error" gutterBottom>
                      {translate('dashboard.heatmap_error', 'Erreur de chargement')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {error}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : heatmapData.length > 0 ? (
              <Heatmap data={heatmapData} />
            ) : (
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: theme.palette.mode === 'dark' ?
                    '0 4px 20px rgba(0, 0, 0, 0.3)' :
                    '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.03)} 0%, ${theme.palette.background.paper} 100%)`
                }}
              >
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        mb: 3
                      }}
                    >
                      <MapPin size={60} color={theme.palette.primary.main} />
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {translate('dashboard.heatmap_welcome', 'Votre carte d\'activité')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                      {translate('dashboard.heatmap_empty', 'Dès que vos utilisateurs commenceront à utiliser l\'application, leurs activités apparaîtront ici sous forme de carte de chaleur.')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {translate('dashboard.heatmap_hint', 'Invitez vos premiers utilisateurs pour voir leur activité !')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} lg={4} sx={{ p: 0 }}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' ?
                  '0 4px 20px rgba(0, 0, 0, 0.3)' :
                  '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                height: 400,
                p: 0,
              }}
            >
            <CardContent sx={{
                p: 0,
                height: '100%',
                '&:last-child': {
                  paddingBottom: 0
                }
              }}>
                <NotificationTimeline />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default DashboardHome;
